import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { JobsPanelState, JobsStatus, jobStatusToString } from "../../../constants/Jobs";
import { FeatureTableContext, JobContext, UserContext } from "../../../Context";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import {
    createNewJobStep,
    exportJobFeaturesWithChangesToSksd,
    getJobRelatedDocumentation,
    hideDocumentationPoints,
    showDocumentationPoints,
    getJobRelatedObjects,
    updateFeatureNfsStatus,
    updateJobStepComment,
} from "../../../utils/jobsUtils";
import InputField from "../../genericComponents/InputField/InputField";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./JobDetails.css";
import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";
import ApproveIcon from "../../../assets/jobs/approve.svg?react";
import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import { getLayerName } from "../../../utils/arcgisUtils";
import { FeatureWithChanges, findFeaturesWithChangesFromJob } from "../../../utils/newVersioningUtils";
import EditedFeaturesForOperativeCompare from "../EditedFeatures/EditedFeaturesForOperativeCompare";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobFailedSksdSendingProps {
    jobObject: Graphic;
    setJobPanelState: (state: JobsPanelState) => void;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobFailedSksdSending: React.FC<JobFailedSksdSendingProps> = ({
    jobObject,
    setJobPanelState,
    setLoading,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const [currentJobStep, setCurrentJobStep] = useState<Graphic | null>(null);
    const [previousJobStep, setPreviousJobStep] = useState<Graphic | null>(null);
    const [historicalSteps, setHistoricalSteps] = useState<Graphic[]>([]);

    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);

    const [comment, setComment] = useState<string | undefined>(undefined);

    const [documentation, setDocumentation] = useState<Graphic[]>([]);
    const [selectedDocumentation, setSelectedDocumentation] = useState<Graphic | null>(null);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const jobsContext = useContext(JobContext);
    const featureTableContext = useContext(FeatureTableContext);
    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const exportToSksd = async (): Promise<boolean> => {
        const result = await exportJobFeaturesWithChangesToSksd(jobFeaturesWithChanges, true);
        if (result.failedFeatures.length > 0) {
            result.failedFeatures.forEach((f) => {
                toast.error(`Feil ved eksport til SKSD: ${f.message}`, {
                    autoClose: false,
                    className: "toast-error",
                });
            });
            return false;
        } else {
            if (result.skippedFeatures.length > 0) {
                toast.info(
                    <>
                        {`Følgende objekter ble ikke eksportert til SKSD`}
                        <br />
                        <br />
                        {result.skippedFeatures.map((skipped) => (
                            <React.Fragment key={skipped.reason}>
                                {`${skipped.reason}:`}
                                <br />
                                {skipped.objectNames.map((name) => (
                                    <React.Fragment key={name}>
                                        {name}
                                        <br />
                                    </React.Fragment>
                                ))}
                                <br />
                            </React.Fragment>
                        ))}
                    </>,
                    {
                        autoClose: false,
                    }
                );
            }

            toast.success("Alle gyldige objekter i jobben har blitt eksportert til SKSD");

            return true;
        }
    };

    const handleSendToSksd = async () => {
        try {
            setLoading(true);

            const commentSuccess = await updateJobStepComment(
                currentJobStep?.attributes.globalid,
                comment ?? "",
                user?.username ?? ""
            );
            if (!commentSuccess) {
                toast.error("Feil ved oppdatering av kommentar på jobbsteget. Vennligst prøv på nytt.", {
                    autoClose: false,
                    className: "toast-error",
                });
                setLoading(false);
                return;
            }

            // Re-run the NFS status update in case it was partially applied during the original QA2 approval.
            // The transition is idempotent: features that already moved out of *_CHANGED are skipped.
            const nfsStatusSuccess = await updateFeatureNfsStatus(jobFeaturesWithChanges);
            if (!nfsStatusSuccess) {
                toast.error("Kunne ikke oppdatere status på objektene i jobben. Prøv igjen.", {
                    autoClose: false,
                    className: "toast-error",
                });
                setLoading(false);
                return;
            }

            const sksdSuccess = await exportToSksd();
            if (sksdSuccess) {
                const jobStepGlobalId = await createNewJobStep(jobObject.attributes.globalid, JobsStatus.Ferdig);
                if (jobStepGlobalId) {
                    toast.success("Jobben er satt til ferdig");
                    dispatch(triggerAlertsRefresh());
                    returnToJobList();
                } else {
                    toast.error("Feil ved oppretting av nytt jobb steg etter eksport til SKSD.", {
                        autoClose: false,
                        className: "toast-error",
                    });
                }
            }
        } catch (error) {
            console.error("Error exporting to SKSD:", error);
        }
        setLoading(false);
    };

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;

        if (job && versionName) {
            setIsLoadingChanges(true);
            const objects = await findFeaturesWithChangesFromJob(job, versionName);
            console.log("Objects with changes from job:", objects);
            setJobFeaturesWithChanges(objects);
            setIsLoadingChanges(false);
        }
    };

    const getDocumentationSeamarkNumber = (graphic: Graphic) => {
        const documentationRelatedObjectGlobalId = graphic.attributes.nautisk_objekt_globalid;
        const documentationRelatedLayerId = graphic.attributes.nautisk_objekt_kilde;
        let documentationRelatedFeature: Graphic | undefined;
        for (let featureWithChange of jobFeaturesWithChanges) {
            if (
                featureWithChange.mainFeature.layerId === documentationRelatedLayerId &&
                featureWithChange.mainFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
            ) {
                documentationRelatedFeature = featureWithChange.mainFeature.versionGraphic;
                break;
            }
            for (let relatedFeature of featureWithChange.relatedFeatures) {
                if (
                    relatedFeature.layerId === documentationRelatedLayerId &&
                    relatedFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
                ) {
                    documentationRelatedFeature = relatedFeature.versionGraphic;
                    break;
                }
            }
            if (documentationRelatedFeature) {
                break;
            }
        }

        return (
            documentationRelatedFeature?.attributes["sjomerke_nr"] ??
            documentationRelatedFeature?.attributes["navn"] ??
            documentationRelatedFeature?.attributes["objectid"] + getLayerName(documentationRelatedLayerId)
        );
    };

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);
            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setCurrentJobStep(activeStep);
                // Find step with highest opprettet_dato less than or equal to activeStep
                // but with a different status (could be same timestamp but different status)
                const maxTime = activeStep.attributes.opprettet_dato;
                let previousStep = null;

                // Sort steps by opprettet_dato in descending order

                for (let step of orderedSteps) {
                    if (
                        step.attributes.globalid !== activeStep.attributes.globalid &&
                        step.attributes.opprettet_dato <= maxTime &&
                        step.attributes.jobb_status !== activeStep.attributes.jobb_status
                    ) {
                        previousStep = step;
                        break;
                    }
                }
                setPreviousJobStep(previousStep);
                setHistoricalSteps(() => {
                    if (jobObject.attributes.status === JobsStatus.Ferdig) {
                        return orderedSteps;
                    } else {
                        return orderedSteps.filter(
                            (step) => step.attributes.globalid !== activeStep.attributes.globalid
                        );
                    }
                });
            }
        });
        getJobRelatedDocumentation(jobObject.attributes.objectid).then((docs) => {
            setDocumentation(docs);
        });
    }, [jobObject]);

    useEffect(() => {
        if (selectedDocumentation) {
            showDocumentationPoints(selectedDocumentation).then((layer) => {
                if (layer) {
                    if (!layer.loaded) {
                        layer.load().then(() => {
                            featureTableContext?.isOpen.set(true);
                            featureTableContext?.activeLayer.set(layer);
                        });
                    } else {
                        featureTableContext?.isOpen.set(true);
                        featureTableContext?.activeLayer.set(layer);
                    }
                }
            });
        }
    }, [featureTableContext?.activeLayer, featureTableContext?.isOpen, selectedDocumentation]);

    useEffect(() => {
        getObjectsConnectedToJob();

        return () => {
            featureTableContext?.activeLayer.set(undefined);
            featureTableContext?.isOpen.set(false);
            hideDocumentationPoints();
        };
    }, []);

    return (
        <>
            <div className="job-details-body">
                <div className="job-details-body-content">
                    <label className="job-details-label">
                        <span className="hidden">{jobObject.attributes.globalid}</span>
                        <span>Jobbnavn</span>
                        <span>{jobObject.attributes.navn}</span>
                    </label>
                    <label className="job-details-label">
                        <span>Beskrivelse</span>
                        <span>{jobObject.attributes.beskrivelse}</span>
                    </label>
                    {previousJobStep && (
                        <label className="job-details-label">
                            <span>Kommentar fra: {previousJobStep.attributes.fullfort_av}</span>
                            <span>{previousJobStep.attributes.kommentar}</span>
                        </label>
                    )}
                    <InputField
                        label="Kommentar"
                        onChange={setComment}
                        inputFieldId={jobObject.attributes.globalid + "comment"}
                        value={comment}
                        type="text"
                        isTextArea
                        textAreaRows={3}
                        maxLength={255}
                    />

                    {historicalSteps.length > 0 && (
                        <Collapsible
                            isOpen={true}
                            setIsOpen={() => {}}
                            title={`Steg med kommentarer (${historicalSteps.length})`}
                            titleClassName="input-field-label"
                        >
                            {historicalSteps.map((step) => (
                                <label key={step.attributes.globalid} className="job-step-container">
                                    <div className="job-step-header">
                                        <span>
                                            <strong>{jobStatusToString(step.attributes.jobb_status)}</strong>
                                        </span>
                                        <span>
                                            {step.attributes.fullfort_av} ({formatDate(step.attributes.oppdatert_dato)})
                                        </span>
                                    </div>
                                    <span>{step.attributes.kommentar ?? "[ingen kommentar]"}</span>
                                </label>
                            ))}
                        </Collapsible>
                    )}

                    <div className="empty-button flex-row small-gap center">
                        <CheckPinIcon />
                        {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Eier</span>
                        <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                    </div>
                    <div className="flex-row space-between">
                        <span className="job-card-key">Oppdatert</span>
                        <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                    </div>
                    {documentation.length > 0 && (
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title={`Dokumentasjon (${documentation.length})`}
                        >
                            {documentation.map((doc) => {
                                console.log("Documentation item:", doc.attributes);
                                return (
                                    <div key={doc.attributes.globalid} className="job-details-documentation-item">
                                        <NfsButton
                                            outlined
                                            className={`w-100 job-details-documentation-button ${
                                                doc === selectedDocumentation ? "selected-documentation" : ""
                                            }`}
                                            onClick={() => {
                                                setSelectedDocumentation(doc);
                                            }}
                                        >
                                            <span>
                                                {getDocumentationSeamarkNumber(doc) +
                                                    " " +
                                                    (doc.attributes.forklaring ?? doc.attributes.beskrivelse ?? "")}
                                            </span>
                                        </NfsButton>
                                    </div>
                                );
                            })}
                        </Collapsible>
                    )}
                </div>
                <EditedFeaturesForOperativeCompare
                    featuresAddedToJob={jobFeaturesWithChanges}
                    isLoadingChanges={isLoadingChanges}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                    jobStatus={jobObject.attributes.status}
                />
            </div>

            {jobObject.attributes.status === JobsStatus.FailedSendingToSksd && (
                <div className="job-details-actions">
                    <div className="job-details-actions-buttons">
                        <NfsButton outlined onClick={handleSendToSksd}>
                            <ApproveIcon />
                            Send til SKSD og bli ferdig med jobben
                        </NfsButton>
                    </div>
                </div>
            )}
        </>
    );
};

export default JobFailedSksdSending;
